class EncoreProductGallery {
  constructor(gallery) {
    this.gallery = gallery;
    this.viewer = gallery.querySelector('[id^="GalleryViewer"]');
    this.track = gallery.querySelector('[id^="Slider-Gallery"]');
    this.raf = null;
    this.currentIndex = 0;
    this.activeMediaId = null;
    this.programmaticScroll = false;
    this.scrollTimer = null;

    if (!this.viewer || !this.track) return;

    this.createControls();
    this.refresh();

    this.track.addEventListener('scroll', () => this.requestUpdate());
    this.resizeObserver = new ResizeObserver(() => this.refresh(false));
    this.resizeObserver.observe(this.track);
    this.mutationObserver = new MutationObserver(() => this.refresh(true));
    this.mutationObserver.observe(this.track, {
      childList: true,
    });
  }

  createControls() {
    this.controls = document.createElement('div');
    this.controls.className = 'encore-gallery-controls';
    this.controls.innerHTML = `
      <button class="encore-gallery-arrow encore-gallery-arrow--prev" type="button" aria-label="Previous media">
        <span aria-hidden="true">&lt;</span>
      </button>
      <button class="encore-gallery-arrow encore-gallery-arrow--next" type="button" aria-label="Next media">
        <span aria-hidden="true">&gt;</span>
      </button>
      <div class="encore-gallery-dots" aria-label="Product media pagination"></div>
    `;

    this.prev = this.controls.querySelector('.encore-gallery-arrow--prev');
    this.next = this.controls.querySelector('.encore-gallery-arrow--next');
    this.dots = this.controls.querySelector('.encore-gallery-dots');

    this.prev.addEventListener('click', () => this.goTo(this.currentIndex - 1));
    this.next.addEventListener('click', () => this.goTo(this.currentIndex + 1));
    this.gallery.append(this.controls);
  }

  getSlides() {
    return Array.from(this.track.querySelectorAll('.product__media-item')).filter((slide) => slide.offsetParent !== null);
  }

  refresh(preferActive = false) {
    const previousMediaId = this.slides?.[this.currentIndex]?.dataset.mediaId || this.activeMediaId;
    this.slides = this.getSlides();
    this.currentIndex = this.getIndexAfterRefresh(previousMediaId, preferActive);
    this.activeMediaId = this.slides[this.currentIndex]?.dataset.mediaId || null;
    this.renderDots();
    this.updateControls();

    if (preferActive && this.slides[this.currentIndex]) {
      this.track.scrollTo({ left: this.currentIndex * this.track.clientWidth });
    }
  }

  getIndexAfterRefresh(previousMediaId, preferActive = false) {
    if (!this.slides.length) return 0;

    const activeIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
    if (preferActive && activeIndex >= 0 && this.slides[activeIndex].dataset.mediaId !== previousMediaId) return activeIndex;

    const previousIndex = this.slides.findIndex((slide) => slide.dataset.mediaId === previousMediaId);
    if (previousIndex >= 0) return previousIndex;

    return Math.min(this.currentIndex, this.slides.length - 1);
  }

  renderDots() {
    if (!this.dots) return;

    this.dots.innerHTML = '';
    this.slides.forEach((slide, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'encore-gallery-dot';
      dot.setAttribute('aria-label', `Go to media ${index + 1}`);
      dot.addEventListener('click', () => this.goTo(index));
      this.dots.append(dot);
    });
  }

  requestUpdate() {
    if (this.programmaticScroll) return;

    if (this.raf) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = null;
      this.currentIndex = this.getIndexFromScroll();
      this.activeMediaId = this.slides[this.currentIndex]?.dataset.mediaId || null;
      this.updateControls();
    });
  }

  getIndexFromScroll() {
    if (!this.slides.length) return 0;

    const slideWidth = this.slides[0].getBoundingClientRect().width || this.track.clientWidth || 1;
    return Math.max(0, Math.min(Math.round(this.track.scrollLeft / slideWidth), this.slides.length - 1));
  }

  updateControls() {
    const hasMultiple = this.slides.length > 1;
    this.controls?.classList.toggle('is-hidden', !hasMultiple);

    if (!hasMultiple) return;

    this.prev.disabled = this.currentIndex <= 0;
    this.next.disabled = this.currentIndex >= this.slides.length - 1;
    this.dots.querySelectorAll('.encore-gallery-dot').forEach((dot, index) => {
      dot.classList.toggle('is-active', index === this.currentIndex);
      dot.toggleAttribute('aria-current', index === this.currentIndex);
    });
  }

  goTo(index) {
    if (!this.slides.length) return;

    const targetIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    const target = this.slides[targetIndex];
    this.currentIndex = targetIndex;
    this.activeMediaId = target.dataset.mediaId || null;
    this.programmaticScroll = true;
    window.clearTimeout(this.scrollTimer);
    this.track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    this.updateControls();
    this.scrollTimer = window.setTimeout(() => {
      this.programmaticScroll = false;
      this.currentIndex = targetIndex;
      this.updateControls();
    }, 360);
  }
}

function initEncoreProductGalleries() {
  document.querySelectorAll('product-info media-gallery').forEach((gallery) => {
    if (gallery.dataset.encoreGalleryReady === 'true') return;
    gallery.dataset.encoreGalleryReady = 'true';
    new EncoreProductGallery(gallery);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initEncoreProductGalleries);
} else {
  initEncoreProductGalleries();
}
